import { useMemo, useState, useEffect } from 'react'

type Environment = 'development' | 'staging' | 'production'

interface EnvironmentConfig {
  label: string
  color: string
}

interface BuildInfo {
  BUILD_DATE: string
  COMMIT_HASH: string
}

const ENV_CONFIGS: Record<Environment, EnvironmentConfig> = {
  development: {
    label: 'ENV',
    color: 'bg-cyan-500',
  },
  staging: {
    label: 'STAGING',
    color: 'bg-fuchsia-500',
  },
  production: {
    label: 'LIVE',
    color: 'bg-lime-500',
  },
}

export function EnvironmentBadge() {
  const [buildInfo, setBuildInfo] = useState<BuildInfo | null>(null)

  useEffect(() => {
    fetch(`/build-info.json?t=${Date.now()}`)
      .then(res => res.json())
      .then(data => setBuildInfo(data))
      .catch(() => setBuildInfo(null))
  }, [])

  const config = useMemo(() => {
    const hostname = window.location.hostname

    let env: Environment
    
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      env = 'development'
    } else if (hostname === 'staging.kombu.team') {
      env = 'staging'
    } else {
      env = 'production'
    }

    return ENV_CONFIGS[env] || ENV_CONFIGS.development
  }, [])

  const formattedDate = useMemo(() => {
    if (!buildInfo?.BUILD_DATE) return null

    const date = new Date(buildInfo.BUILD_DATE)
    if (isNaN(date.getTime())) return null

    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }, [buildInfo])

  const shortHash = useMemo(() => {
    if (!buildInfo?.COMMIT_HASH) return null
    return buildInfo.COMMIT_HASH.substring(0, 7)
  }, [buildInfo])

  return (
    <div className="fixed bottom-4 left-4 z-50">
      <div className={`inline-flex items-center px-3 py-1.5 rounded-full ${config.color} shadow-lg`}>
        <span className="text-xs font-semibold text-white tracking-wider">
          {config.label}
        </span>
        {formattedDate && (
          <>
            <span className="text-white mx-2">·</span>
            <span className="text-xs font-medium text-white">
              {formattedDate}
            </span>
          </>
        )}
        {shortHash && (
          <>
            <span className="text-white mx-2">·</span>
            <span className="text-xs font-mono text-white">
              {shortHash}
            </span>
          </>
        )}
      </div>
    </div>
  )
}
